import {
  AtendimentoIndividualInput,
  RetificacaoAtendimentoInput,
  RetificarAtendimentoMutation,
  RetificarAtendimentoVacinacaoMutation,
  TipoAtendimentoProfissional,
} from 'graphql/types.generated'
import { partition } from 'lodash'
import { ErrorObject } from 'util/validation'
import { convertMedicoesAnterioresQueryRetificacaoToForm } from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { convertFinalizacaoQueryRetificacaoToForm } from 'view/atendimentos/detail/soap/finalizacao/converter-finalizacao'
import { convertObjetivoQueryRetificacaoToForm } from 'view/atendimentos/detail/soap/objetivo'
import { convertPlanoQueryRetificacaoToForm } from 'view/atendimentos/detail/soap/plano/converter-plano'
import { convertSubjetivoQueryRetificacaoToForm } from 'view/atendimentos/detail/soap/subjetivo/converter-subjetivo'

import { SoapState } from '../atendimentos/atendimento-individual/model'
import {
  AtendimentoIndividualRetificacaoModel,
  FlagsRetificacao,
  RetificarMutations,
} from './model-retificacaoAtendimento'
import { RetificacaoStorageModel } from './useJustificativaRetificacaoStorage'

export const initialValueRetificacao = (
  atendimentoIndividualRetificacao: AtendimentoIndividualRetificacaoModel,
  retificacaoStorage: RetificacaoStorageModel,
  cidadaoCpfCns: string | undefined
): SoapState => {
  const {
    evolucaoObjetivo,
    ivcf,
    marcadorConsumoAlimentar,
    medicoes,
    evolucaoPlano,
    evolucaoSubjetivo,
    orientacoes,
    arquivosAnexados,
    atestados,
  } = atendimentoIndividualRetificacao
  const [medicaoAtual, medicoesAnteriores] = partition(medicoes, (medicao) => !medicao.medicaoAnterior)

  return {
    openedAccordions: ['S', 'O', 'A', 'P', 'D'],
    subjetivo: convertSubjetivoQueryRetificacaoToForm(evolucaoSubjetivo),
    medicoesAnteriores: convertMedicoesAnterioresQueryRetificacaoToForm(medicoesAnteriores),
    plano: convertPlanoQueryRetificacaoToForm(evolucaoPlano, orientacoes, atestados, cidadaoCpfCns),
    objetivo: convertObjetivoQueryRetificacaoToForm(
      evolucaoObjetivo,
      marcadorConsumoAlimentar,
      medicaoAtual[0],
      ivcf,
      arquivosAnexados
    ),
    avaliacao: {
      problemasECondicoes: [],
    },
    finalizacao: convertFinalizacaoQueryRetificacaoToForm(atendimentoIndividualRetificacao),
    acompanhamentoPreNatal: {
      idadeGestacional: null,
    },
    retificacaoAtendimento: {
      justificativa: retificacaoStorage?.justificativa,
      historicoCabecalhoId: retificacaoStorage?.historicoCabecalhoId,
    },
  }
}

export function getAtendimentoIndividualInputErrorsFromRetificacao(
  error: ErrorObject<RetificacaoAtendimentoInput>
): ErrorObject<AtendimentoIndividualInput> | undefined {
  const atendimentoRetificadoInput = error?.atendimentoRetificadoInput

  if (!!atendimentoRetificadoInput && typeof atendimentoRetificadoInput === 'object') {
    return atendimentoRetificadoInput
  }
}

function isRetificacaoHabilitada(
  tipoAtendimentoProf: TipoAtendimentoProfissional | undefined,
  flagsRetificacao: FlagsRetificacao
): boolean {
  switch (tipoAtendimentoProf) {
    case TipoAtendimentoProfissional.VACINACAO:
      return flagsRetificacao.RETIFICACAO_ATENDIMENTO_VACINACAO_ENABLED
    case TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS:
      return flagsRetificacao.PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED
    case TipoAtendimentoProfissional.CONSULTA:
      return flagsRetificacao.RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED
    default:
      return false
  }
}

export const TIPOS_ATENDIMENTO_RETIFICAVEIS_RNDS = new Set<TipoAtendimentoProfissional>([
  TipoAtendimentoProfissional.CONSULTA,
  TipoAtendimentoProfissional.VACINACAO,
])

// TODO (RNG: 29771) Remover isCancelado
export function permitirExibirBotaoRetificacao(
  tipoAtendimentoProf: TipoAtendimentoProfissional | undefined,
  isCancelado: boolean,
  hasPermissaoRegistrarAtendimento: boolean,
  hasPermissaoRegistrarAtendimentoVacinacao: boolean,
  flagsRetificacao: FlagsRetificacao
): boolean {
  const retificacaoHabilitada = isRetificacaoHabilitada(tipoAtendimentoProf, flagsRetificacao)
  const hasPermissao = isAtendimentoProfissionalVacinacao(tipoAtendimentoProf)
    ? hasPermissaoRegistrarAtendimentoVacinacao
    : hasPermissaoRegistrarAtendimento

  return !isCancelado && hasPermissao && retificacaoHabilitada
}

export function mostrarAcoesAtendimentoInativo(
  tipoAtendimentoProf: TipoAtendimentoProfissional | undefined,
  isInativoOrRetificado: boolean,
  flagsRetificacao: FlagsRetificacao
): boolean {
  return isInativoOrRetificado && isRetificacaoHabilitada(tipoAtendimentoProf, flagsRetificacao)
}

// TODO (RNG: 29771) Remover isCancelado
export function permitirExcluirAtendimento(
  isAcessoLotacaoCriadora: boolean,
  idAtendRecente: boolean,
  isCancelado: boolean
): boolean {
  return isAcessoLotacaoCriadora && idAtendRecente && !isCancelado
}

export function isAtendimentoProfissionalVacinacao(tipoAtendProf: TipoAtendimentoProfissional | undefined): boolean {
  return tipoAtendProf === TipoAtendimentoProfissional.VACINACAO
}

export function isRetificarAtendimentoMutation(data: RetificarMutations): data is RetificarAtendimentoMutation {
  return (data as RetificarAtendimentoMutation).retificarAtendimento !== undefined
}

export function isRetificarAtendimentoVacinacaoMutation(
  data: RetificarMutations
): data is RetificarAtendimentoVacinacaoMutation {
  return (data as RetificarAtendimentoVacinacaoMutation).retificarAtendimentoVacinacao !== undefined
}
